import { store } from "src/store/store";
import { getIsInDetachMode } from "src/duck/selectors";
import { detachModeChanged } from "src/duck/actions";
import environment from "src/utils/environment";

const remote = window.require("@electron/remote");
const { getCurrentWindow } = remote;
const mainWindow = getCurrentWindow?.();

export class DetachMode {
  constructor({ toggleDetachButton, toggleVideoButton, handleFade }) {
    this.unsubscribe = null;
    this.timeoutRef = null;
    this.handleFade = handleFade;
    this.toggleDetachButton = toggleDetachButton;
    this.toggleVideoButton = toggleVideoButton;

    this.originalSize = { width: null, height: null };
    this.originalMinimumSize = { width: null, height: null };
    this.originalPosition = { x: null, y: null };

    this.detachSize = { width: null, height: null };
    this.detachPosition = { x: null, y: null };
  }

  clearListeners = () => {
    window.document.removeEventListener("mouseenter", this.renderButtons);
    window.document.removeEventListener("mouseleave", this.hideButtons);
    clearTimeout(this.timeoutRef);

    if (environment.platform !== "darwin" && this.unsubscribe) {
      this.unsubscribe();
    }
  };

  waitForFade = async () => {
    return new Promise((resolve) => {
      this.timeoutRef = setTimeout(() => {
        clearTimeout(this.timeoutRef);
        this.timeoutRef = null;
        resolve();
      }, 500);
    });
  };

  handleDetachModeChange = () => {
    const isInDetachMode = getIsInDetachMode(store.getState());
    if (!isInDetachMode) this.handleUndetach();
  };

  renderButtons = () => {
    this.toggleDetachButton(true);
    this.toggleVideoButton(true);
  };

  hideButtons = () => {
    this.toggleDetachButton(false);
    this.toggleVideoButton(false);
  };

  handleDetach = async () => {
    this.handleFade();
    await this.waitForFade();

    if (environment.platform !== "darwin") {
      this.unsubscribe = store.subscribe(this.handleDetachModeChange);
    }
    this.renderButtons();

    window.document.addEventListener("mouseenter", this.renderButtons);
    window.document.addEventListener("mouseleave", this.hideButtons);
    window.document.body.style["-webkit-app-region"] = "drag";

    const [originMinWidth, originMinHeight] = mainWindow.getMinimumSize();
    this.originalMinimumSize.width = originMinWidth;
    this.originalMinimumSize.height = originMinHeight;

    const [originX, originY] = mainWindow.getPosition();
    this.originalPosition.x = originX;
    this.originalPosition.y = originY;

    const [originWidth, originHeight] = mainWindow.getSize();
    this.originalSize.width = originWidth;
    this.originalSize.height = originHeight;

    mainWindow.setAlwaysOnTop(true);

    mainWindow.setMinimumSize(156, 112);
    mainWindow.setSize(
      this.detachSize.width || 320,
      this.detachSize.height || 229,
      false
    );
    if (this.detachPosition.x && this.detachPosition.y) {
      mainWindow.setPosition(
        this.detachPosition.x,
        this.detachPosition.y,
        false
      );
    }

    if (environment.platform === "darwin") {
      mainWindow.setClosable(false);
    }
    const isFullScreen = mainWindow.isFullScreen();
    if (isFullScreen) mainWindow.setFullScreen(false);

    mainWindow.setFullScreenable(false);
    mainWindow.setMinimizable(false);
    mainWindow.setMaximizable(false);

    store.dispatch(detachModeChanged(true));
  };

  handleUndetach = async (fade = true) => {
    if (fade) {
      this.handleFade();
      await this.waitForFade();
    }

    this.hideButtons();

    window.document.body.style["-webkit-app-region"] = "no-drag";

    const [originWidth, originHeight] = mainWindow.getSize();
    this.detachSize.width = originWidth;
    this.detachSize.height = originHeight;

    const [originX, originY] = mainWindow.getPosition();
    this.detachPosition.x = originX;
    this.detachPosition.y = originY;

    mainWindow.setAlwaysOnTop(false);

    mainWindow.setMinimumSize(
      this.originalMinimumSize.width,
      this.originalMinimumSize.height
    );

    mainWindow.setPosition(
      this.originalPosition.x,
      this.originalPosition.y,
      false
    );

    mainWindow.setSize(
      this.originalSize.width,
      this.originalSize.height,
      false
    );

    if (environment.platform === "darwin") {
      mainWindow.setClosable(true);
    }
    mainWindow.setFullScreenable(true);
    mainWindow.setMinimizable(true);
    mainWindow.setMaximizable(true);

    store.dispatch(detachModeChanged(false));
    this.clearListeners();
  };
}
